import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Check,
  Loader2,
  MapPin,
  Megaphone,
  Phone,
  Share2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { categories, categoryName, cities } from "@/lib/data";
import { supabase } from "@/integrations/supabase/client";
import { conditionOptionsFor } from "@/lib/specs";
import { azError } from "@/lib/az-errors";
import type { ListingRow } from "@/lib/listings";

export const Route = createFileRoute("/_authenticated/elan-idare/$id")({
  head: () => ({
    meta: [
      { title: "Elanı idarə et — Albazar.az" },
      { name: "description", content: "Elan parametrlərini, əlaqə üsullarını və yerləşməni idarə edin." },
      { property: "og:title", content: "Elanı idarə et — Albazar.az" },
      { property: "og:description", content: "Elan parametrləri və reklam imkanları." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ManageListing,
});

const storages = ["64 GB", "128 GB", "256 GB", "512 GB", "1 TB"];
const colors = ["Space Gray", "Gümüşü", "Qızılı", "Qara", "Ağ", "Mavi"];
const deliveries = ["Çatdırılma yoxdur", "Şəhər daxili çatdırılma", "Bütün ölkə üzrə"];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 border-b border-border py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between">
      <Label className="text-sm text-muted-foreground">{label}</Label>
      <div className="sm:w-64">{children}</div>
    </div>
  );
}

function ManageListing() {
  const { id } = Route.useParams();
  const { user } = Route.useRouteContext();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["listing", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select(
          "id, user_id, title, description, price, negotiable, category, city, phone, whatsapp, images, badge, status, views, created_at, specs",
        )
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return (data as ListingRow | null) ?? null;
    },
  });

  const [form, setForm] = useState({
    currency: "AZN",
    price: "",
    model: "",
    condition: "",
    storage: "256 GB",
    color: "Space Gray",
    delivery: deliveries[0]!,
    phone: "",
    contactPref: "call",
    city: "",
  });
  const [hydrated, setHydrated] = useState(false);

  if (isLoading) {
    return (
      <div className="container-x grid place-items-center py-24">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!data || data.user_id !== user.id) {
    return (
      <div className="container-x py-24 text-center">
        <h1 className="text-2xl font-extrabold">Elan tapılmadı</h1>
        <p className="mt-2 text-sm text-muted-foreground">Bu elan silinib və ya sizə aid deyil.</p>
        <Link to="/profil">
          <Button className="mt-5 rounded-full">Profilə qayıt</Button>
        </Link>
      </div>
    );
  }

  if (!hydrated) {
    setHydrated(true);
    setForm((f) => ({
      ...f,
      price: String(data.price ?? ""),
      model: data.title,
      phone: data.phone ?? "",
      city: data.city,
      condition: ((data as unknown as { specs?: Record<string, string> | null }).specs?.["condition"] ?? ""),
    }));
  }

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const conditions = conditionOptionsFor(data.category);

  const save = async () => {
    setSaving(true);
    const currentSpecs = { ...((data as unknown as { specs?: Record<string, string> | null }).specs ?? {}) };
    if (form.condition) currentSpecs["condition"] = form.condition;
    else delete currentSpecs["condition"];
    const nextSpecs = currentSpecs;
    const { error } = await supabase
      .from("listings")
      .update({
        price: form.price ? Number(form.price) : 0,
        phone: form.phone.trim(),
        city: form.city,
        specs: nextSpecs,
      })
      .eq("id", data.id);
    setSaving(false);
    if (error) {
      toast.error(azError(error));
      return;
    }
    toast.success("Dəyişikliklər yadda saxlanıldı");
  };

  const toggleActive = async () => {
    const next = data.status === "active" ? "draft" : "active";
    const { error } = await supabase.from("listings").update({ status: next }).eq("id", data.id);
    if (error) {
      toast.error(azError(error));
      return;
    }
    toast.success(next === "active" ? "Elan aktivləşdirildi" : "Elan deaktiv edildi");
    void navigate({ to: "/profil" });
  };

  const share = async () => {
    const url = `${window.location.origin}/elan/${data.id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: data.title, url });
        return;
      } catch {
        /* istifadəçi ləğv etdi */
      }
    }
    await navigator.clipboard.writeText(url);
    toast.success("Link kopyalandı");
  };

  const sub = categories.find((c) => c.slug === data.category);

  return (
    <div className="container-x max-w-3xl pb-32 pt-4">
      <header className="flex items-center justify-between gap-3">
        <button
          type="button"
          aria-label="Geri"
          onClick={() => void navigate({ to: "/profil" })}
          className="grid size-10 place-items-center rounded-full border border-border"
        >
          <ArrowLeft className="size-5" />
        </button>
        <h1 className="truncate text-base font-bold">{data.title}</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Paylaş"
            onClick={() => void share()}
            className="grid size-10 place-items-center rounded-full border border-border"
          >
            <Share2 className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            className="text-sm font-bold text-primary"
          >
            {saving ? "Saxlanılır..." : "Yadda saxla"}
          </button>
        </div>
      </header>

      <section className="mt-6 rounded-3xl border border-border bg-card p-5 shadow-card">
        <h2 className="font-bold">Parametrlər</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <span className="rounded-full bg-success/15 px-3 py-1 text-xs font-semibold text-success">
            {categoryName(data.category)}
          </span>
          {sub?.icon && (
            <span className="rounded-full bg-success/15 px-3 py-1 text-xs font-semibold text-success">
              {sub.icon} Mobil telefon və aksesuarlar
            </span>
          )}
        </div>

        <div className="mt-4">
          <Field label="Valyuta">
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger className="h-11! rounded-2xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["AZN", "USD", "EUR"].map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Qiymət">
            <Input
              type="number"
              min="0"
              value={form.price}
              onChange={(e) => set("price", e.target.value)}
              className="h-11 rounded-2xl"
            />
          </Field>
          <Field label="Model">
            <Input
              value={form.model}
              onChange={(e) => set("model", e.target.value)}
              placeholder="iPhone Xs"
              className="h-11 rounded-2xl"
            />
          </Field>
          {conditions.length > 0 && (
          <Field label="Vəziyyəti">
            <Select value={form.condition} onValueChange={(v) => set("condition", v)}>
              <SelectTrigger className="h-11! rounded-2xl">
                <SelectValue placeholder="Vəziyyəti seçin" />
              </SelectTrigger>
              <SelectContent>
                {conditions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          )}
          <Field label="Yaddaş tutumu">
            <Select value={form.storage} onValueChange={(v) => set("storage", v)}>
              <SelectTrigger className="h-11! rounded-2xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                {storages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Rəng">
            <Select value={form.color} onValueChange={(v) => set("color", v)}>
              <SelectTrigger className="h-11! rounded-2xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                {colors.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Çatdırılma">
            <Select value={form.delivery} onValueChange={(v) => set("delivery", v)}>
              <SelectTrigger className="h-11! rounded-2xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                {deliveries.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </section>

      <section className="mt-5 rounded-3xl border border-border bg-card p-5 shadow-card">
        <h2 className="font-bold">Telefon</h2>
        <div className="mt-3 flex items-center gap-3 rounded-2xl border border-border px-4">
          <Phone className="size-4 text-primary" />
          <Input
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="+994 77 603 03 35"
            className="h-12 border-0 px-0 shadow-none focus-visible:ring-0"
          />
        </div>
        <RadioGroup
          value={form.contactPref}
          onValueChange={(v) => set("contactPref", v)}
          className="mt-4 gap-3"
        >
          <label className="flex items-center gap-3 rounded-2xl border border-border p-3 text-sm">
            <RadioGroupItem value="call" id="pref-call" />
            Yalnız zənglə
          </label>
          <label className="flex items-center gap-3 rounded-2xl border border-border p-3 text-sm">
            <RadioGroupItem value="chat" id="pref-chat" />
            Yalnız Albazar çatı ilə
          </label>
        </RadioGroup>
      </section>

      <section className="mt-5 rounded-3xl border border-border bg-card p-5 shadow-card">
        <h2 className="font-bold">Xəritə</h2>
        <Select value={form.city} onValueChange={(v) => set("city", v)}>
          <SelectTrigger className="mt-3 h-12! rounded-2xl">
            <span className="flex items-center gap-2">
              <MapPin className="size-4 text-primary" />
              <SelectValue placeholder="Şəhər seçin" />
            </span>
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="mt-3 overflow-hidden rounded-2xl border border-border">
          <iframe
            title="Xəritə"
            className="h-56 w-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src={`https://www.google.com/maps?q=${encodeURIComponent(form.city || "Bakı")}&output=embed`}
          />
        </div>
      </section>

      <section className="mt-5 rounded-3xl border border-border bg-card p-5 text-sm shadow-card">
        <div className="flex justify-between border-b border-border py-2">
          <span className="text-muted-foreground">Yenilənmə tarixi</span>
          <span className="font-semibold">{new Date(data.created_at).toLocaleDateString("az-AZ")}</span>
        </div>
        <div className="flex justify-between border-b border-border py-2">
          <span className="text-muted-foreground">Dərc olunma tarixi</span>
          <span className="font-semibold">{new Date(data.created_at).toLocaleDateString("az-AZ")}</span>
        </div>
        <div className="flex justify-between py-2">
          <span className="text-muted-foreground">ID</span>
          <span className="font-semibold">{data.id.slice(0, 8).toUpperCase()}</span>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void share()}
          className="mt-4 h-12 w-full rounded-2xl border-success text-success hover:bg-success/10 hover:text-success"
        >
          <Share2 className="size-4" /> Dostlarla paylaş
        </Button>
      </section>

      <div className="fixed inset-x-0 bottom-16 z-40 border-t border-border bg-background/95 p-3 backdrop-blur md:bottom-0">
        <div className="container-x flex max-w-3xl gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => void toggleActive()}
            className="h-12 flex-1 rounded-2xl border-success text-success hover:bg-success/10 hover:text-success"
          >
            <Check className="size-4" />
            {data.status === "active" ? "Deaktiv et" : "Aktiv et"}
          </Button>
          <Link to="/elan-reklam/$id" params={{ id }} search={{ plan: "vip" as const, days: 7 }} className="flex-1">
            <Button className="h-12 w-full rounded-2xl bg-[hsl(215_90%_52%)] text-white hover:bg-[hsl(215_90%_46%)]">
              <Megaphone className="size-4" /> Reklam et
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}